import TextButton from 'components/Luxkit/Button/TextButton'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import HeadingTextBlock from 'components/Luxkit/TextBlocks/HeadingTextBlock'
import React, { useEffect, useRef, useState } from 'react'
import ContentfulFormField from './ContentfulFormField'
import Group from 'components/utils/Group'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import { themeClassName } from 'lib/theme/themeUtils'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { formToObject } from 'lib/forms/formToObject'
import { sendContentfulFormData } from 'api/promo'
import { mediaQueryUp } from 'components/utils/breakpoint'

const FormContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  background-color: ${props => props.theme.palette.neutral.default.eight};

  &.background-color-white {
    background-color: ${props => props.theme.palette.neutral.default.eight};
  }

  &.background-color-grey {
    background-color: ${props => props.theme.palette.neutral.default.seven};
  }
`

const CentredGroup = styled(Group)`
  margin: ${rem(16)};

  ${mediaQueryUp.tablet} {
    max-width: ${rem(500)};
  }


  &.background-color-white {
    background-color: ${props => props.theme.palette.neutral.default.eight};
  }

  &.background-color-grey {
    background-color: ${props => props.theme.palette.neutral.default.seven};
  }
`

function ContentfulForm(props: Omit<App.ComponentForm, 'contentTypeId'>) {
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const { title, subtitle, slug, buttonText, successMessage, components, backgroundColor } = props

  const formRef = useRef<HTMLFormElement>(null)

  useEffect(() => {
    formRef.current?.reset()
  }, [])

  async function handleFormSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const currentForm = e.currentTarget
    const data = formToObject<object>(currentForm)

    try {
      await sendContentfulFormData({ slug, data })
      currentForm.reset()
      setErrorMessage(null)
      setIsSubmitted(true)
    } catch (error) {
      console.error(error)
      setErrorMessage('Failed to send the form. Please try again.')
      setIsSubmitted(false)
      return
    }
  }

  return (
    <form onSubmit={handleFormSubmit} ref={formRef}>
      <FormContainer className={clsx(`background-color-${backgroundColor}`, themeClassName(backgroundColor === 'dark' ? 'inverse' : 'default'))}>
        <CentredGroup direction="vertical" id="centered-group" gap={8} className={clsx(`background-color-${backgroundColor}`)}>
          <HeadingTextBlock variant="heading2" colour="neutral-two" >{title}</HeadingTextBlock>
          <BodyText variant="large" colour="neutral-two">{subtitle}</BodyText>
          {components.map((component, index) => (
            <ContentfulFormField key={index} {...component} />
          ))}
          <TextButton
            kind="primary"
            type="submit"
            disabled={isSubmitted}
            >
            {isSubmitted ? 'Sent successfully' : buttonText}
          </TextButton>
          {errorMessage && <BodyTextBlock variant="medium" colour="critical">{errorMessage}</BodyTextBlock>}
          {isSubmitted && <BodyTextBlock variant="medium" colour="neutral-two">{successMessage}</BodyTextBlock>}
        </CentredGroup>
      </FormContainer>
    </form>
  )
}

export default ContentfulForm
